import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLocale } from "../contexts/LocaleContext";
import {
  LayoutDashboard, Key, Users, Shield, FileText, Package, ShoppingBag, Star, MessageSquare,
  RefreshCw, Tag, Palette, FileEdit, Wrench, AlertTriangle, Activity, Zap, LogOut, ArrowLeft, LifeBuoy, Cpu, Sun, Moon, Globe, ChevronDown, Image as ImageIcon, ListOrdered, Camera, FolderTree, Megaphone
} from "lucide-react";

const NAV = {
  developer: [
    { to: "/developer", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/developer/keys", label: "Integration Keys", icon: Key },
    { to: "/developer/limits", label: "Limits & Settings", icon: Cpu },
    { to: "/developer/ai-usage", label: "AI Usage", icon: Zap },
    { to: "/developer/users", label: "Users", icon: Users },
    { to: "/developer/roles", label: "Roles & Permissions", icon: Shield },
    { to: "/developer/branding", label: "Branding", icon: Palette },
    { to: "/developer/homepage", label: "Homepage Editor", icon: ImageIcon },
    { to: "/developer/menus", label: "Header & Footer Menus", icon: ListOrdered },
    { to: "/developer/content", label: "Site Content", icon: FileText },
    { to: "/developer/pages", label: "Custom Pages", icon: FileEdit },
    { to: "/developer/maintenance", label: "Maintenance", icon: Wrench },
    { to: "/developer/support", label: "Support Tickets", icon: LifeBuoy },
    { to: "/developer/security", label: "Security & IP Logs", icon: AlertTriangle },
    { to: "/developer/audit", label: "Audit Log", icon: Activity },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/orders", label: "Orders", icon: Package },
    { to: "/admin/products", label: "Products", icon: ShoppingBag },
    { to: "/admin/approvals", label: "Pending Approvals", icon: FileEdit },
    { to: "/admin/customers", label: "Customers (CRM)", icon: Users },
    { to: "/admin/reviews", label: "Reviews", icon: Star },
    { to: "/admin/enquiries", label: "Chatbot Enquiries", icon: MessageSquare },
    { to: "/admin/refunds", label: "Refunds & Exchanges", icon: RefreshCw },
    { to: "/admin/discounts", label: "Discounts", icon: Tag },
    { to: "/admin/popup", label: "Discount Popup", icon: Megaphone },
    { to: "/admin/categories", label: "Categories", icon: FolderTree },
    { to: "/admin/developer-support", label: "Developer Support", icon: LifeBuoy },
  ],
  shop_assistant: [
    { to: "/shop-dashboard", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/shop-dashboard/add-product", label: "Add Product (AI)", icon: Zap },
    { to: "/shop-dashboard/camera", label: "Snap & Submit (Camera)", icon: Camera },
    { to: "/shop-dashboard/add-product-manual", label: "Add Product (Manual)", icon: Package },
    { to: "/shop-dashboard/categories", label: "Categories", icon: FolderTree },
    { to: "/shop-dashboard/my-products", label: "My Submissions", icon: FileText },
  ],
};

const LABELS = { developer: "Developer Console", admin: "Store Admin", shop_assistant: "Shop Assistant" };

const LANGS = [
  { value: "en", label: "EN · English" },
  { value: "ar", label: "AR · العربية" },
];

export default function DashboardLayout({ role }) {
  const { user, logout } = useAuth();
  const { isDark, toggle: toggleTheme } = useTheme();
  const { lang, setLang } = useLocale();
  const loc = useLocation();
  const nav = useNavigate();
  const items = NAV[role] || [];
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const currentLangLabel = (LANGS.find((l) => l.value === lang) || LANGS[0]).label;

  return (
    <div className="dashboard-scope min-h-screen grid grid-cols-1 md:grid-cols-[260px_1fr]">
      <aside className={`dashboard-sidebar hidden md:flex flex-col ${isDark ? "bg-[#070707] border-r border-white/5" : "bg-white border-r border-black/8"}`}>
        <div className={`px-6 py-7 ${isDark ? "border-b border-white/5" : "border-b border-black/8"}`}>
          <div className="overline text-copper">{LABELS[role]}</div>
          <div className={`font-display text-xl mt-1 ${isDark ? "text-white" : "text-dark"}`}>Namos Antiques</div>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {items.map((it) => {
            const active = it.end ? loc.pathname === it.to : loc.pathname === it.to || loc.pathname.startsWith(it.to + "/");
            return (
              <Link key={it.to} to={it.to} data-testid={`nav-${it.to.replaceAll("/", "-")}`} className={`sidebar-link ${active ? "active" : ""}`}>
                <it.icon size={15}/> {it.label}
              </Link>
            );
          })}
        </nav>
        <div className={`p-5 text-xs ${isDark ? "border-t border-white/5" : "border-t border-black/8"}`}>
          <div className={isDark ? "text-white/80" : "text-gray-800"}>{user?.name}</div>
          <div className={`truncate ${isDark ? "text-white/50" : "text-gray-500"}`}>{user?.email}</div>
          <div className="overline mt-2 text-copper">{user?.role}</div>

          {/* Theme + Language switchers */}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              data-testid="dashboard-theme-toggle"
              onClick={toggleTheme}
              title="Toggle theme"
              className={`flex items-center justify-center gap-1.5 px-2 py-2 border text-[10px] uppercase tracking-luxe transition-colors ${isDark ? "border-white/15 text-white/85 hover:border-copper hover:text-white bg-white/[0.02]" : "border-black/15 text-gray-700 hover:border-copper hover:text-dark bg-white"}`}
              style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#374151" }}
            >
              {isDark ? <><Sun size={11}/> Light</> : <><Moon size={11}/> Dark</>}
            </button>

            {/* Custom language dropdown for guaranteed visibility */}
            <div className="relative" ref={langRef}>
              <button
                type="button"
                data-testid="dashboard-lang-toggle"
                onClick={() => setLangOpen((v) => !v)}
                className={`w-full flex items-center justify-between gap-1 px-2 py-2 border text-[10px] uppercase tracking-luxe transition-colors ${isDark ? "border-white/15 text-white/85 hover:border-copper bg-white/[0.02]" : "border-black/15 text-gray-700 hover:border-copper bg-white"}`}
                style={{ color: isDark ? "rgba(255,255,255,0.9)" : "#374151" }}
              >
                <span className="flex items-center gap-1 truncate"><Globe size={11}/> {currentLangLabel}</span>
                <ChevronDown size={11} className="opacity-60 shrink-0"/>
              </button>
              {langOpen && (
                <div
                  data-testid="dashboard-lang-menu"
                  className={`absolute bottom-full left-0 right-0 mb-1 z-30 border shadow-xl ${isDark ? "bg-[#0E0E0E] border-white/10" : "bg-white border-black/10"}`}
                >
                  {LANGS.map((l) => (
                    <button
                      key={l.value}
                      type="button"
                      data-testid={`dashboard-lang-${l.value}`}
                      onClick={() => { setLang(l.value); setLangOpen(false); }}
                      className={`block w-full text-left px-3 py-2 text-[10px] uppercase tracking-luxe transition-colors ${isDark ? "hover:bg-white/5" : "hover:bg-[#FAF7F2]"} ${lang === l.value ? "text-copper" : (isDark ? "text-white/85" : "text-gray-700")}`}
                      style={{ color: lang === l.value ? "#B89766" : (isDark ? "rgba(255,255,255,0.85)" : "#374151") }}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bottom action buttons */}
          <div className="flex gap-3 mt-3 flex-wrap">
            <button data-testid="back-to-site" onClick={() => nav("/")} className={`text-[11px] uppercase tracking-luxe flex items-center gap-1 ${isDark ? "text-white/70 hover:text-white" : "text-gray-700 hover:text-dark"}`}><ArrowLeft size={11}/> Site</button>
            <button data-testid="dashboard-logout" onClick={() => { logout(); nav("/"); }} className={`text-[11px] uppercase tracking-luxe flex items-center gap-1 ml-auto ${isDark ? "text-white/70 hover:text-white" : "text-gray-700 hover:text-dark"}`}><LogOut size={11}/> Logout</button>
          </div>
        </div>
      </aside>
      <main className="px-6 md:px-10 py-8 md:py-10 max-w-[1400px] w-full">
        <Outlet />
      </main>
    </div>
  );
}
